import { ClaimZelleRequest } from '@bybeam/platform-types';
import Message from '@platform-ui-common/components/Message/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useForm, {
  defaultRequiredErrorMessage,
  fieldUpdate,
  requiredField,
  ValidationMode,
} from '@platform-ui-common/hooks/useForm/index.js';
import { isEmail } from '@platform-ui-common/utilities/email.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import ClaimForm from '@platform-ui/claim/components/ClaimForm/index.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import useSubmitClaim from '@platform-ui/claim/hooks/utils/useClaimWithRedirect.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import cx from 'classnames';
import { useHistory } from 'react-router';

const emailAddress = (email: string, bold = false): JSX.Element => (
  <span className={cx(bold && 'font-bold', 'break-all')}>{email ?? 'n/a'}</span>
);

function EmailConfirmation(): JSX.Element {
  const { user } = useAuth();
  const partner = usePartner();
  const history = useHistory();
  const { fulfillment, method, payload, setPayload, setMethod } = useClaim<ClaimZelleRequest>();
  const { submitClaim, error, loading } = useSubmitClaim(fulfillment, method, payload);
  const { formData, dispatch, errors, trySubmit } = useForm(
    {
      email: payload?.email ?? '',
    },
    {
      email: requiredField(({ email }) => {
        if (!isEmail(email)) return 'Please enter a valid email address';
        if (email !== user?.core?.email)
          return [
            `The email you've entered does not match the email address used to create your ${partner.name} account.`,
            `Please add ${user?.core?.email} to your Zelle account to continue or choose a new payment method.`,
          ];
        return undefined;
      }, defaultRequiredErrorMessage('Email')),
    },
    ValidationMode.OnSubmit,
  );
  const onReset = (): void => {
    setPayload(null);
    setMethod(null);
    history.push(makeRoute(ClaimRoutes.PAYMENT_METHOD, { fulfillmentId: fulfillment.id }));
  };

  const errorMessages = Object.values(errors).flat();

  return (
    <ClaimForm
      header="Please confirm your email address before submitting"
      subHeader={
        <>
          For security purposes, we will send your funds through Zelle to the email address,{' '}
          {emailAddress(user?.core?.email, true)}, you used to create your {partner.name} account.
        </>
      }
      onSubmit={(): void => {
        trySubmit(submitClaim);
      }}
      onReset={onReset}
      loading={loading}
      isSubmit
      helperText={
        <div className="flex flex-col gap-y-4">
          <Message variant="info">
            Failing to add {emailAddress(user?.core?.email)} to your Zelle account may result in a
            successful submission but will cause a transaction error and a delay in receiving your
            funds.
          </Message>
          {error?.retryable && <Message variant="warning">{error?.message}</Message>}
        </div>
      }
      errors={
        !!errorMessages?.length && (
          <span className="flex flex-col gap-y-4">
            {errorMessages.map((str: string) => (
              <span key={str}>{str}</span>
            ))}
          </span>
        )
      }
    >
      <Typography variant="largeBody">Please enter your Zelle email address below:</Typography>
      <img alt="Zelle Logo" src="/assets/zelle/zelle_logo.png" />
      <TextInput
        id="email"
        label="Zelle Email Address"
        value={formData.email}
        onChange={(value): void => dispatch(fieldUpdate('email', value))}
        required
        error={!!errors?.email}
      />
    </ClaimForm>
  );
}

export default EmailConfirmation;
